/**
 * Master key provisioning - the provisioning concern.
 *
 * Generates the camera's `master_key`, seals it into a password-protected backup
 * (via {@link sealMasterKey}), uploads it to the cloud, persists the returned
 * backup locally, preserves the derived key, and caches the plaintext `master_key`.
 *
 * The `master_key` is never sent in the clear: the app generates an ephemeral
 * RSA-2048 keypair per provisioning, hands the camera its public key
 * (`app_public_key`), and the camera returns `master_key_enc` (RSA-OAEP/SHA-256
 * ciphertext) which is decrypted locally with the in-memory private key.
 */

import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { setCameraStorageKey } from 'src/services/cam-cloud/recordings';
import { real_id } from 'src/helpers/interface_helpers';
import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
import { camera_storage_passphrases } from 'src/components/Camera/stores/camera_storage_passphrases';
import { camera_storage_resolved_keys } from 'src/components/Camera/stores/camera_storage_resolved_keys';
import type { ProvisionResult } from './types/master-key';
import { masterKeyToBytes, sealMasterKey } from './master-key-management';
import { base64ToBytes, bytesToBase64 } from './encoding';

/** RPC method that asks a camera to generate its recording master key. */
const PAIRING_REQUEST_METHOD = 'Camera.PairingRequest';

/** Modulus length (bits) of the ephemeral RSA keypair used for the pairing exchange. */
const RSA_MODULUS_LENGTH = 2048;

/** Hash used by RSA-OAEP (and its MGF1) for the pairing exchange - matches the camera's mbedtls setup. */
const RSA_HASH = 'SHA-256';

/**
 * Generates an ephemeral RSA-OAEP keypair for a single pairing exchange. The
 * private key stays in memory and is discarded once `master_key` is decrypted.
 */
const generatePairingKeyPair = (): Promise<CryptoKeyPair> => {
	return crypto.subtle.generateKey(
		{
			name: 'RSA-OAEP',
			modulusLength: RSA_MODULUS_LENGTH,
			publicExponent: new Uint8Array([1, 0, 1]),
			hash: RSA_HASH,
		},
		true,
		['encrypt', 'decrypt'],
	);
};

/**
 * Exports a public key as base64-encoded SPKI DER (the camera's
 * `ParseDEROrPEMOrFile` accepts raw DER, so no PEM wrapping is needed).
 */
const exportPublicKeySpki = async (publicKey: CryptoKey): Promise<string> => {
	const spki = new Uint8Array(await crypto.subtle.exportKey('spki', publicKey));
	return bytesToBase64(spki);
};

/**
 * Decrypts the camera's `master_key_enc` (base64 RSA-OAEP/SHA-256 ciphertext)
 * with the ephemeral private key and returns the base64 `master_key`.
 *
 * The camera RSA-encrypts the raw 32 `master_key` bytes, so the decrypted
 * plaintext is base64-encoded to form the `master_key`. Returns `null` when the
 * result is not a valid 32-byte key.
 */
const decryptMasterKey = async (
	privateKey: CryptoKey,
	masterKeyEncB64: string,
): Promise<string | null> => {
	const ciphertext = base64ToBytes(masterKeyEncB64);
	const plaintext = new Uint8Array(
		await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, ciphertext as BufferSource),
	);

	const masterKey = bytesToBase64(plaintext);
	return masterKeyToBytes(masterKey) ? masterKey : null;
};

/**
 * Runs the camera pairing request that generates the device's `master_key` (the
 * per-recording keys are later derived from it, see `recording-key.ts`).
 *
 * Generates an ephemeral RSA-2048 keypair, sends the public key as base64 SPKI
 * DER in the `Camera.PairingRequest` call (`app_public_key` param), and decrypts
 * the returned `master_key_enc` with the private key.
 *
 * @param device - The camera device.
 * @returns The generated base64 `master_key` secret, or `null` when the request or
 * decryption fails.
 */
export const generateMasterKeyRequest = async (device: Device): Promise<string | null> => {
	const channel = device?.channel || 0;
	try {
		const keyPair = await generatePairingKeyPair();
		const publicKeySpki = await exportPublicKeySpki(keyPair.publicKey);

		const res: SettingRequest = await gen2Request(device, PAIRING_REQUEST_METHOD, {
			id: channel,
			app_public_key: publicKeySpki,
		});

		const masterKeyEnc = res?.data?.master_key_enc;
		if (!res?.isok || typeof masterKeyEnc !== 'string') return null;

		return await decryptMasterKey(keyPair.privateKey, masterKeyEnc);
	} catch (error) {
		console.error('[master-key-provisioning] pairing key exchange failed', error);
		return null;
	}
};

/**
 * Provisions a password-protected master key for a camera: generates `master_key`
 * on the device, seals it with the password key (PBKDF2, fresh random salt packed
 * into the blob), uploads the encrypted backup to the cloud, then persists the
 * returned backup locally (keyed by the backend's `createdAt` era), preserves the
 * password-key hex, and caches the decrypted `master_key` in memory.
 *
 * @param device - The camera device.
 * @param password - The raw user password.
 * @returns The provisioning values ({@link ProvisionResult}) for display.
 * @throws When key generation or the cloud upload fails.
 */
export const provisionMasterKey = async (device: Device, password: string): Promise<ProvisionResult> => {
	const deviceId = real_id(device.id);

	const masterKey = await generateMasterKeyRequest(device);
	if (!masterKey) {
		throw new Error('Failed to generate master key');
	}

	const { blob, hmac, passwordKeyHex } = await sealMasterKey(masterKey, password);

	// The backend persists the backup and assigns its `createdAt` era; the key
	// store and passphrase are keyed by that same era.
	const stored = await setCameraStorageKey({ mac: deviceId, blob, hmac });

	camera_storage_keys.setEncrypted(deviceId, stored);
	camera_storage_passphrases.setPasswordKey(deviceId, passwordKeyHex, stored.createdAt);
	camera_storage_resolved_keys.setKey(deviceId, stored.createdAt, masterKey);

	return { masterKey, passwordKeyHex, blob, hmac };
};
